import { cn } from "@/lib/utils";
import { DOT_COLORS, OUTLINED_DOTS, type DotColor } from "@/data/skillGuide";

interface LevelDotProps {
  color: DotColor;
  size?: "sm" | "md" | "lg";
  /** On a dark (forest) background: adds a beige ring so black/brown stay visible. */
  onDark?: boolean;
  /**
   * Accessible name. Pass it only when no text label sits next to the dot;
   * otherwise the dot is decorative (the visible label names it).
   */
  label?: string;
  className?: string;
}

const SIZES = { sm: "h-2.5 w-2.5", md: "h-3.5 w-3.5", lg: "h-5 w-5" };

/** Solid schedule dot from the owner's color guide. Always pair with a visible text label. */
const LevelDot = ({ color, size = "md", onDark = false, label, className }: LevelDotProps) => {
  const outlined = OUTLINED_DOTS.includes(color);
  return (
    <span
      className={cn("inline-block shrink-0 rounded-full align-middle", SIZES[size], className)}
      style={{
        backgroundColor: `var(--level-${color}, ${DOT_COLORS[color].hex})`,
        border: outlined && !onDark ? "1.5px solid hsl(var(--velto-forest))" : undefined,
        boxShadow: onDark ? "0 0 0 2px hsl(var(--velto-beige))" : undefined,
      }}
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
    />
  );
};

export default LevelDot;
